/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type {
  AgentConversationHistory,
  AgentConversationTurn,
  AgentEvent,
  AgentKnowledgeGraphActivity,
  AgentKnowledgeGraphNode,
  AgentKnowledgeGraphNodeKind,
  AgentKnowledgeGraphNodeStatus,
  AgentKnowledgeGraphState,
} from '../../types';

const REQUEST_NODE_ID = 'request';
const SKILL_HUB_ID = 'hub-skills';
const KNOWLEDGE_HUB_ID = 'hub-knowledge';
const TOOL_HUB_ID = 'hub-tools';

const HUBS: Array<AgentKnowledgeGraphNode> = [
  { id: SKILL_HUB_ID, kind: 'hub', label: 'Skills', family: 'skill' },
  { id: KNOWLEDGE_HUB_ID, kind: 'hub', label: 'Knowledge', family: 'knowledge' },
  { id: TOOL_HUB_ID, kind: 'hub', label: 'Tools', family: 'tool' },
];

const HUB_BY_KIND: Partial<Record<AgentKnowledgeGraphNodeKind, string>> = {
  skill: SKILL_HUB_ID,
  knowledge: KNOWLEDGE_HUB_ID,
  tool: TOOL_HUB_ID,
};
const TOOL_KIND_BY_NAME: Partial<Record<string, AgentKnowledgeGraphNodeKind>> = {
  read_skill: 'skill',
  read_kb: 'knowledge',
  search_kb: 'knowledge',
};

export const createKnowledgeGraph = (prompt?: string | null): AgentKnowledgeGraphState => {
  const centerLabel = cleanLabel(prompt) ?? 'New request';
  return {
    centerLabel,
    sequence: 0,
    nodes: [
      {
        id: REQUEST_NODE_ID,
        kind: 'request',
        label: centerLabel,
        family: 'request',
        status: prompt ? 'active' : undefined,
        hot: Boolean(prompt),
      },
      ...HUBS,
    ],
    links: HUBS.map((hub) => ({ source: REQUEST_NODE_ID, target: hub.id, kind: 'hub' })),
    activity: prompt
      ? [
          {
            id: 'activity-0',
            kind: 'message',
            label: 'User request',
            detail: centerLabel,
            status: 'active',
          },
        ]
      : [],
    latestNodeId: prompt ? REQUEST_NODE_ID : undefined,
  };
};

export const applyEventToKnowledgeGraph = (
  state: AgentKnowledgeGraphState,
  event: AgentEvent,
): AgentKnowledgeGraphState => {
  switch (event.type) {
    case 'tool_started':
      return spawnGraphNode(
        state,
        {
          kind: categorizeTool(event.tool_name),
          label: eventLabel(event, 'Tool started'),
          toolName: event.tool_name ?? undefined,
          detail: eventDetail(event),
          status: 'active',
        },
        'start',
      );
    case 'tool_finished':
      return completeGraphNode(state, {
        kind: categorizeTool(event.tool_name),
        label: eventLabel(event, 'Tool finished'),
        toolName: event.tool_name ?? undefined,
        detail: eventDetail(event),
        status: eventStatus(event.status),
      });
    case 'skill_activated':
      return completeGraphNode(state, {
        kind: 'skill',
        label: eventLabel(event, 'Skill activated'),
        toolName: event.tool_name ?? undefined,
        detail: eventDetail(event),
        status: 'ok',
      });
    case 'kb_read':
      return completeGraphNode(state, {
        kind: 'knowledge',
        label: eventLabel(event, 'Knowledge read'),
        toolName: event.tool_name ?? undefined,
        detail: eventDetail(event),
        status: 'ok',
      });
    case 'sandbox_result':
      return completeGraphNode(state, {
        kind: 'tool',
        label: eventLabel(event, 'Sandbox result'),
        toolName: event.tool_name ?? undefined,
        detail: eventDetail(event),
        status: eventStatus(event.status),
      });
    case 'artifact_ready':
      return spawnGraphNode(
        state,
        {
          kind: 'output',
          label: event.filename ?? event.artifact_kind ?? 'Artifact ready',
          detail: eventDetail(event),
          status: 'ok',
        },
        'instant',
      );
    case 'map_ready':
      return spawnGraphNode(
        state,
        {
          kind: 'output',
          label: event.map_view?.title ?? 'Map ready',
          detail: eventDetail(event),
          status: 'ok',
        },
        'instant',
      );
    case 'assistant_text_chunk':
      return state;
    case 'done':
      return completeRequestNode(state);
    case 'error':
      return spawnGraphNode(
        state,
        {
          kind: 'output',
          label: 'Agent error',
          detail: event.content ?? undefined,
          status: 'failed',
        },
        'instant',
      );
    default:
      return state;
  }
};

export const startKnowledgeGraphTurn = (
  state: AgentKnowledgeGraphState,
  prompt: string,
): AgentKnowledgeGraphState => {
  const centerLabel = cleanLabel(prompt) ?? state.centerLabel;
  const nextSequence = state.sequence + 1;

  return {
    ...state,
    centerLabel,
    sequence: nextSequence,
    nodes: state.nodes.map((node) =>
      node.id === REQUEST_NODE_ID
        ? {
            ...node,
            label: centerLabel,
            status: 'active',
            hot: true,
            activation: nextSequence,
          }
        : { ...node, hot: false },
    ),
    activity: [
      ...state.activity,
      {
        id: `activity-${nextSequence}`,
        kind: 'message',
        label: 'User request',
        detail: centerLabel,
        status: 'active',
      },
    ],
    latestNodeId: REQUEST_NODE_ID,
  };
};

export const buildKnowledgeGraphFromHistory = (
  history: AgentConversationHistory | undefined,
): AgentKnowledgeGraphState => {
  const turns = history?.turns ?? [];
  const latestPrompt = [...turns].reverse().find((turn) => cleanLabel(turn.prompt))?.prompt;
  let state = createKnowledgeGraph(latestPrompt);

  for (const turn of turns) {
    state = appendActivity(state, {
      kind: 'message',
      label: `Turn ${turn.turn_index + 1}`,
      detail: cleanLabel(turn.prompt),
      status: 'ok',
    });

    const events = eventsFromConversationTurn(turn);
    for (const event of events) {
      state = applyEventToKnowledgeGraph(state, event);
    }
  }

  return state;
};

const spawnGraphNode = (
  state: AgentKnowledgeGraphState,
  node: Omit<AgentKnowledgeGraphNode, 'id'>,
  phase: AgentKnowledgeGraphActivity['phase'] = 'instant',
): AgentKnowledgeGraphState => {
  const nextSequence = state.sequence + 1;
  const existingNode = findReusableNode(state, node);
  if (existingNode) {
    return {
      ...state,
      sequence: nextSequence,
      nodes: state.nodes.map((candidate) =>
        candidate.id === existingNode.id
          ? {
              ...candidate,
              label: isGenericLifecycleLabel(node.label) ? candidate.label : node.label,
              detail: node.detail ?? candidate.detail,
              status: node.status,
              hot: true,
              activation: nextSequence,
            }
          : { ...candidate, hot: false },
      ),
      activity: [
        ...state.activity,
        {
          id: `activity-${nextSequence}`,
          kind: node.kind,
          label: node.label,
          detail: node.detail,
          status: node.status,
          toolName: node.toolName,
          phase,
        },
      ],
      latestNodeId: existingNode.id,
    };
  }

  const id = `${node.kind}-${nextSequence}`;
  const family = node.family ?? familyForKind(node.kind);
  const targetNode = { ...node, id, family, hot: true, activation: nextSequence };
  const source = HUB_BY_KIND[node.kind] ?? REQUEST_NODE_ID;
  const linkKind = node.kind === 'output' ? 'output' : 'activity';

  return {
    ...state,
    sequence: nextSequence,
    nodes: [...coolNodes(state.nodes), targetNode],
    links: [...state.links, { source, target: id, kind: linkKind }],
    activity: [
      ...state.activity,
      {
        id: `activity-${nextSequence}`,
        kind: node.kind,
        label: node.label,
        detail: node.detail,
        status: node.status,
        toolName: node.toolName,
        phase,
      },
    ],
    latestNodeId: id,
  };
};

const completeRequestNode = (state: AgentKnowledgeGraphState): AgentKnowledgeGraphState => {
  const nextSequence = state.sequence + 1;
  return {
    ...state,
    sequence: nextSequence,
    nodes: state.nodes.map((node) =>
      node.id === REQUEST_NODE_ID
        ? { ...node, status: 'ok', hot: true, activation: nextSequence }
        : { ...settleCompletedNode(node), hot: false },
    ),
    activity: state.activity.map(settleCompletedActivity),
    latestNodeId: REQUEST_NODE_ID,
  };
};

const settleCompletedNode = (node: AgentKnowledgeGraphNode): AgentKnowledgeGraphNode => {
  if (node.status !== 'active' && node.status !== 'pending') return node;
  return { ...node, status: 'ok' };
};

const settleCompletedActivity = (
  activity: AgentKnowledgeGraphActivity,
): AgentKnowledgeGraphActivity => {
  if (activity.status !== 'active' && activity.status !== 'pending') return activity;
  return { ...activity, status: 'ok' };
};

const completeGraphNode = (
  state: AgentKnowledgeGraphState,
  completion: {
    kind: AgentKnowledgeGraphNodeKind;
    label: string;
    toolName?: string;
    detail?: string;
    status: AgentKnowledgeGraphNodeStatus;
  },
): AgentKnowledgeGraphState => {
  const match = [...state.nodes]
    .reverse()
    .find(
      (node) =>
        node.kind === completion.kind &&
        (node.status === 'active' || node.status === 'pending') &&
        nodesMatch(node, completion),
    );

  if (!match) {
    return spawnGraphNode(state, completion, 'complete');
  }

  const nextSequence = state.sequence + 1;
  return {
    ...state,
    sequence: nextSequence,
    nodes: state.nodes.map((node) =>
      node.id === match.id
        ? {
            ...node,
            detail: completion.detail ?? node.detail,
            status: completion.status,
            hot: true,
            activation: nextSequence,
          }
        : { ...node, hot: false },
    ),
    activity: [
      ...state.activity,
      {
        id: `activity-${nextSequence}`,
        kind: completion.kind,
        label: completion.label,
        detail: completion.detail,
        status: completion.status,
        toolName: completion.toolName,
        phase: 'complete',
      },
    ],
    latestNodeId: match.id,
  };
};

const appendActivity = (
  state: AgentKnowledgeGraphState,
  activity: Omit<AgentKnowledgeGraphActivity, 'id'>,
): AgentKnowledgeGraphState => {
  const nextSequence = state.sequence + 1;
  return {
    ...state,
    sequence: nextSequence,
    activity: [...state.activity, { ...activity, id: `activity-${nextSequence}` }],
  };
};

export const eventsFromConversationTurn = (turn: AgentConversationTurn): Array<AgentEvent> => {
  if (turn.events?.length) {
    return turn.events;
  }
  const events: Array<AgentEvent> = [];
  collectEvents(turn.messages, events);

  const resultText = textFromUnknown(turn.result);
  if (resultText) {
    events.push({ type: 'assistant_text_chunk', content: resultText });
    events.push({ type: 'done' });
  } else if (events.some((event) => event.type === 'assistant_text_chunk')) {
    events.push({ type: 'done' });
  }

  return events;
};

const collectEvents = (value: unknown, events: Array<AgentEvent>) => {
  if (!value) return;
  if (Array.isArray(value)) {
    for (const entry of value) {
      collectEvents(entry, events);
    }
    return;
  }
  if (typeof value !== 'object') return;

  const record = value as Record<string, unknown>;
  const partKind = stringValue(record.part_kind) ?? stringValue(record.type);
  const toolName = stringValue(record.tool_name) ?? stringValue(record.name);

  const event = eventFromRecord(record, partKind, toolName);
  if (event) events.push(event);

  if (record.parts) collectEvents(record.parts, events);
  if (record.content && typeof record.content !== 'string') collectEvents(record.content, events);
};

const eventFromRecord = (
  record: Record<string, unknown>,
  partKind: string | undefined,
  toolName: string | undefined,
): AgentEvent | undefined => {
  if (partKind === 'tool-call' && toolName) {
    return {
      type: 'tool_started',
      tool_name: toolName,
      summary: toolRecordSummary(toolName, record),
    };
  }
  if (partKind === 'tool-return' && toolName) {
    return {
      type: 'tool_finished',
      tool_name: toolName,
      summary: toolRecordSummary(toolName, record),
      status: toolReturnStatus(record),
      content: textFromUnknown(record.content),
    };
  }
  if (partKind === 'retry-prompt' && toolName) {
    return {
      type: 'tool_finished',
      tool_name: toolName,
      summary: toolRecordSummary(toolName, record),
      status: 'retry',
      content: textFromUnknown(record.content),
    };
  }
  if (partKind === 'text') {
    const content = stringValue(record.content);
    return content ? { type: 'assistant_text_chunk', content } : undefined;
  }
  if (partKind === 'thinking' || partKind === 'thinking_text_chunk') {
    const content = stringValue(record.content);
    return content ? { type: 'thinking_text_chunk', content } : undefined;
  }
  if (record.role === 'assistant' && typeof record.content === 'string') {
    return { type: 'assistant_text_chunk', content: record.content };
  }
  return undefined;
};

const eventLabel = (event: AgentEvent, fallback: string) =>
  cleanLabel(event.summary) ??
  cleanLabel(event.tool_name ? humanizeToolName(event.tool_name) : undefined) ??
  cleanLabel(event.filename) ??
  cleanLabel(event.artifact_kind) ??
  fallback;

const eventDetail = (event: AgentEvent) => {
  if (event.type === 'sandbox_result') {
    return [event.stdout, event.result_repr].filter(Boolean).join('\n') || undefined;
  }
  if (event.type === 'artifact_ready') {
    return [event.artifact_kind, event.byte_size ? `${event.byte_size} bytes` : undefined]
      .filter(Boolean)
      .join(' / ');
  }
  return event.content ?? undefined;
};

const eventStatus = (status: AgentEvent['status']): AgentKnowledgeGraphNodeStatus => {
  if (status === 'error') return 'failed';
  if (status === 'retry') return 'retry';
  return 'ok';
};

const toolReturnStatus = (record: Record<string, unknown>): AgentEvent['status'] => {
  const content = textFromUnknown(record.content)?.toLowerCase() ?? '';
  if (content.includes('error') || content.includes('failed') || content.includes('traceback')) {
    return 'error';
  }
  return 'ok';
};

const toolRecordSummary = (toolName: string, record: Record<string, unknown>) =>
  cleanLabel(record.summary) ?? toolArgumentLabel(toolName, record) ?? humanizeToolName(toolName);

const toolArgumentLabel = (
  toolName: string,
  record: Record<string, unknown>,
): string | undefined => {
  const args = toolArgs(record);
  if (!args || typeof args !== 'object' || Array.isArray(args)) return undefined;

  const argsRecord = args as Record<string, unknown>;
  if (toolName === 'read_skill') {
    return readableSlugLabel(stringValue(argsRecord.name));
  }
  if (toolName === 'read_kb') {
    return stringValue(argsRecord.path_or_name) ?? stringValue(argsRecord.path);
  }
  return undefined;
};

const toolArgs = (record: Record<string, unknown>): unknown => {
  const args = record.args ?? record.arguments ?? record.tool_args;
  if (typeof args !== 'string') return args;

  try {
    return JSON.parse(args) as unknown;
  } catch {
    return undefined;
  }
};

const labelsMatch = (left: string, right: string) =>
  normalizeLabel(left) === normalizeLabel(right) ||
  normalizeLabel(left).includes(normalizeLabel(right)) ||
  normalizeLabel(right).includes(normalizeLabel(left));

const nodesMatch = (
  node: AgentKnowledgeGraphNode,
  completion: {
    label: string;
    toolName?: string;
  },
) =>
  (completion.toolName && node.toolName === completion.toolName) ||
  labelsMatch(node.label, completion.label);

const findReusableNode = (
  state: AgentKnowledgeGraphState,
  node: Omit<AgentKnowledgeGraphNode, 'id'>,
) =>
  [...state.nodes]
    .reverse()
    .find(
      (candidate) =>
        candidate.kind === node.kind &&
        candidate.kind !== 'hub' &&
        candidate.kind !== 'request' &&
        reusableNodesMatch(candidate, node),
    );

const reusableNodesMatch = (
  node: AgentKnowledgeGraphNode,
  nextNode: Omit<AgentKnowledgeGraphNode, 'id'>,
) => {
  if (!nextNode.toolName || node.toolName !== nextNode.toolName) {
    return normalizeLabel(node.label) === normalizeLabel(nextNode.label);
  }
  if (nextNode.toolName === 'read_skill' || nextNode.toolName === 'read_kb') {
    return normalizeLabel(node.label) === normalizeLabel(nextNode.label);
  }
  return true;
};

const isGenericLifecycleLabel = (label: string) =>
  /^(read_skill|read_kb|search_kb|execute_code|[\w-]+)\s+returned\s+successfully$/i.test(label);

const cleanLabel = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const stringValue = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const textFromUnknown = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return cleanLabel(value);
  if (Array.isArray(value)) return value.map(textFromUnknown).filter(Boolean).join('\n');
  if (typeof value !== 'object') return undefined;

  const record = value as Record<string, unknown>;
  return (
    stringValue(record.text) ??
    stringValue(record.content) ??
    stringValue(record.result) ??
    stringValue(record.message)
  );
};

const humanizeToolName = (toolName: string) =>
  toolName.replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase());

const readableSlugLabel = (value: string | undefined) => value?.replaceAll('-', ' ');

const categorizeTool = (toolName: string | null | undefined): AgentKnowledgeGraphNodeKind =>
  toolName ? (TOOL_KIND_BY_NAME[toolName] ?? 'tool') : 'tool';

const familyForKind = (kind: AgentKnowledgeGraphNodeKind): AgentKnowledgeGraphNode['family'] => {
  if (kind === 'hub') return undefined;
  return kind;
};

const normalizeLabel = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '');

const coolNodes = (nodes: Array<AgentKnowledgeGraphNode>) =>
  nodes.map((node) => ({ ...node, hot: false }));
